import React, {
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  AddOrUpdateEvidenceGroupRequest,
  DOCUMENT_TYPE,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { MultiSelectForm } from 'src/components/Reusable/Forms/MultiSelectForm'
import { getPluralization } from 'src/Utils/globalHelpers'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { SelectControlsRowItem } from 'src/components/Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import { useLocation } from 'react-router'
import { EvidenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useControls } from 'src/components/async/model/control'
import { NoRecommendedTemplates } from 'src/components/ModalForms/Frameworks/AddFramework/NoRecommendedTemplates'
import { useInAudit } from 'src/context/AuditContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import log from 'loglevel'
import isFunction from 'lodash/isFunction'
import { EvidencePayload } from '@trustero/trustero-api-web/lib/evidence/service_pb'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import {
  useAlertDialogModal,
  ShowAlertDialog,
} from 'src/components/ModalForms/useAlertDialogModal'
import { useBulkCreateEvidenceForControls } from 'src/components/async/evidence/useBulkCreateEvidence'
import { useSharepointLinkerShareUrl } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.hooks'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { Message } from 'src/components/Reusable/Message/Message'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import {
  useInvalidateEvidenceGroups,
  useLinkControlsToEvidence,
} from '../evidence.hooks'

export enum LinkEvidenceToControlsModalType {
  CREATE = 'create',
  UPDATE = 'update',
}

export const LinkEvidenceToControlsModal = ({
  requests,
  evidenceId,
  onHideFunc,
  onCreateFunc,
  existingControlIds,
  payloads,
  type = LinkEvidenceToControlsModalType.CREATE,
}: {
  requests: AddOrUpdateEvidenceGroupRequest[]
  evidenceId?: string
  payloads?: EvidencePayload[]
  onHideFunc?: () => void
  onCreateFunc?: () => void
  existingControlIds?: string[]
  type?: LinkEvidenceToControlsModalType
}): JSX.Element => {
  const runSharepointLinker = useSharepointLinkerShareUrl()
  const showAlertDialog: ShowAlertDialog = useAlertDialogModal()
  const {
    filesSelected,
    setFilesSelected,
    setSubjectIds,
    shareUrlObject,
    setShareUrlObject,
    onSubmit: onSPDMSubmit,
  } = useSharepointFilePickerContext()
  const {
    filesSelected: gdriveFilesSelectedRaw,
    setFilesSelected: setGDriveFileSelected,
    setSubjectIds: setGDriveSubjectIds,
    onSubmit: onGDriveSubmit,
  } = useGoogleDriveContext()
  const {
    filesSelected: confluenceFilesSelected,
    setFilesSelected: setConfluenceFilesSelected,
    setSubjectIds: setConfluenceSubjectIds,
    onSubmit: onConfluenceSubmit,
  } = useConfluenceFilePickerContext()
  const location = useLocation()
  const { auditId } = useInAudit()
  const indexMutate = useInvalidateEvidenceGroups()
  const showMutate = useGrpcRevalidateByMethod()
  const isIndex = location.pathname.includes(EvidenceAbsoluteRoutes.INDEX)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const show = useIsShowModal(ModalFormId.LINK_EVIDENCE_TO_CONTROLS)
  const onHide = () => {
    isFunction(onHideFunc) && onHideFunc()
    setSelectedControls([])
  }
  const hide = useHideModal({
    modalId: ModalFormId.LINK_EVIDENCE_TO_CONTROLS,
    onHide,
  })
  const { data, isLoading, error } = useControls()
  const bulkCreateEvidenceForControls = useBulkCreateEvidenceForControls()
  const linkControlsToEvidence = useLinkControlsToEvidence()

  useEffect(() => {
    setSubjectIds(selectedControls)
    setGDriveSubjectIds(selectedControls)
    setConfluenceSubjectIds(selectedControls)
  }, [
    selectedControls,
    setSubjectIds,
    setGDriveSubjectIds,
    setConfluenceSubjectIds,
  ])

  const controls = useMemo(() => {
    if (!data) return []
    return data.getItemsList().filter((control) => {
      return !existingControlIds?.includes(control.getId())
    })
  }, [data, existingControlIds])

  const onHideHandler = useCallback(() => {
    setFilesSelected([])
    setShareUrlObject({
      shareUrl: '',
      caption: '',
      isViaRequest: false,
    })
    hide()
  }, [hide, setFilesSelected, setShareUrlObject])

  const onSubmit: FormEventHandler = useCallback(
    async (e: React.MouseEvent<HTMLFormElement>) => {
      let hasError = false
      try {
        e.preventDefault()
        setIsSubmitting(true)
        if (type === LinkEvidenceToControlsModalType.UPDATE) {
          await linkControlsToEvidence(evidenceId ?? '', selectedControls)
          await showMutate(
            AttachmentPromiseClient.prototype.getEvidenceControls,
          )
        } else if (filesSelected.length > 0) {
          const relevantDate = requests[0].getEvidenceGroup()?.getRelevantDate()
          await onSPDMSubmit({
            caption:
              filesSelected.length > 1 ? '' : filesSelected[0]?.caption || '',
            isViaRequest: false,
            relevantDate,
          })
          setFilesSelected([])
        } else if ((gdriveFilesSelectedRaw ?? []).length > 0) {
          const relevantDate = requests[0].getEvidenceGroup()?.getRelevantDate()
          await onGDriveSubmit({
            caption:
              (gdriveFilesSelectedRaw ?? []).length > 1
                ? ''
                : (gdriveFilesSelectedRaw ?? [])[0]?.caption || '',
            isViaRequest: false,
            relevantDate,
          })
          if (setGDriveFileSelected) {
            setGDriveFileSelected([])
          }
        } else if ((confluenceFilesSelected ?? []).length > 0) {
          const relevantDate = requests[0].getEvidenceGroup()?.getRelevantDate()
          await onConfluenceSubmit({
            caption:
              (confluenceFilesSelected ?? []).length > 1
                ? ''
                : (confluenceFilesSelected ?? [])[0]?.title || '',
            isViaRequest: false,
            relevantDate: relevantDate,
            // Multi-select Confluence submission
            node: confluenceFilesSelected[0],
            nodes: confluenceFilesSelected,
          })
          if (setConfluenceFilesSelected) {
            setConfluenceFilesSelected([])
          }
        } else if (shareUrlObject.shareUrl != '') {
          await runSharepointLinker(
            shareUrlObject.shareUrl,
            DOCUMENT_TYPE.EVIDENCE,
            shareUrlObject.isViaRequest,
            shareUrlObject.caption,
            selectedControls,
          )
        } else {
          await bulkCreateEvidenceForControls({
            payloads: payloads ?? [],
            controlIds: selectedControls,
          })
        }
      } catch (error) {
        hasError = true
        log.error('Error linking evidenceGroups to Controls', error)
        if (!selectedControls.length) {
          showInfoToast('Error adding evidence')
        } else {
          showInfoToast('Error linking evidence to controls')
        }
      } finally {
        if (isIndex) {
          indexMutate()
        }
        onCreateFunc?.()
        setIsSubmitting(false)
        onHideHandler()
        if (
          hasError &&
          (filesSelected.length > 0 || shareUrlObject.shareUrl != '')
        ) {
          showAlertDialog({
            title: 'Unable to Add Evidence',
            body: 'We couldn’t upload your document. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
          })
        }
      }
    },
    [
      payloads,
      bulkCreateEvidenceForControls,
      requests,
      selectedControls,
      onSPDMSubmit,
      showMutate,
      onHideHandler,
      isIndex,
      filesSelected,
      setFilesSelected,
      gdriveFilesSelectedRaw,
      onGDriveSubmit,
      setGDriveFileSelected,
      onConfluenceSubmit,
      confluenceFilesSelected,
      setConfluenceFilesSelected,
      showAlertDialog,
      linkControlsToEvidence,
      type,
      evidenceId,
      onCreateFunc,
      indexMutate,
      runSharepointLinker,
      shareUrlObject,
    ],
  )

  const multiSelectFormProps = useMemo(
    () => ({
      formId: ModalFormId.LINK_EVIDENCE_TO_CONTROLS,
      loading: isLoading,
      gridTemplateColumnStyling:
        'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)',
      gridItems: controls.length ? (
        <>
          {controls.map((control) => (
            <SelectControlsRowItem
              key={control.getId()}
              control={control}
              setSelectedControlIds={setSelectedControls}
              selectedControlIds={selectedControls}
              disabled={isSubmitting}
            />
          ))}
        </>
      ) : (
        <NoRecommendedTemplates templateType={'controls'} />
      ),
      numberSelectedText: `${selectedControls.length} ${getPluralization(
        'control',
        selectedControls.length,
      )} selected`,
      onSubmit,
    }),
    [isLoading, isSubmitting, controls, selectedControls, onSubmit],
  )

  const showWarning = !!auditId && isIndex && selectedControls.length === 0

  if (!data) {
    if (error) {
      log.error(
        'Error fetching controls in link evidence to controls modal',
        error,
      )
    }
    return <></>
  }

  return (
    <ModalForm
      show={show}
      hide={onHideHandler}
      formId={ModalFormId.LINK_EVIDENCE_TO_CONTROLS}
      size="xl"
      title="Select Relevant Controls"
      description="The evidence will be linked to each of these controls"
      submitText={`Select ${selectedControls.length} ${getPluralization(
        'control',
        selectedControls.length,
      )}`}
      showOverflow
      disableSubmitButton={isSubmitting}
      busy={isSubmitting}
    >
      {showWarning && (
        <Message
          messageType={MessageType.WARNING}
          messageText={`Warning: this evidence won't be visible here. Link to a control in audit to make it visible. Evidence is only shown in an audit if a control in that audit is using it.`}
        />
      )}
      <MultiSelectForm {...multiSelectFormProps} />
    </ModalForm>
  )
}
